import {
  ModuloDTO,
  getGetModuliQueryOptions,
  getModuli,
} from '@framework/api-client';
import {
  AssignableExpressionInput,
  ConditionGroupInput,
  Datatype,
  IWidgetComponentProps,
  useTreeReferencesContext,
} from '@framework/engine';
import {
  AsyncSelectInput,
  CheckboxInput,
  FormItem,
  TextInput,
} from '@framework/form';
import { ColumnSortDirections, getPagination } from '@framework/ui';
import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { IModuleTriggerWidget } from './IModuleTriggerWidget';

const InputBorder = styled.div`
  border: 1px solid ${props => props.theme.colorBorder};
  border-radius: 6px;
  padding: 8px;
`;

export interface IModuleTriggerConfigComponentProps
  extends IWidgetComponentProps<IModuleTriggerWidget> {}

export function ModuleTriggerConfigComponent(
  props: IModuleTriggerConfigComponentProps,
) {
  const needUniqueModuleCode = useWatch({
    name: 'needUniqueModuleCode',
    exact: true,
  });

  const { references } = useTreeReferencesContext();

  const expressionReferences = useMemo(() => {
    if (!needUniqueModuleCode) return [];
    return references.filter(r => r.field.type === Datatype.String);
  }, [needUniqueModuleCode, references]);

  return (
    <div>
      <FormItem name="code" label="Codice">
        <TextInput name="code" />
      </FormItem>
      <FormItem name="moduleId" label="Modulo">
        <AsyncSelectInput
          style={{ width: 'calc(100% - 100px)' }}
          placeholder="Cerca e aggiungi Modulo..."
          queryFn={getModuli}
          queryOptions={getGetModuliQueryOptions}
          initialParams={getPagination({
            page: 0,
            pageSize: 20,
            direction: ColumnSortDirections.ascend,
            sort: 'titolo',
          })}
          searchByField={'titolo'}
          asOption={(module: ModuloDTO) => ({
            value: module.id!,
            label: module.titolo ?? 'Modulo senza nome',
          })}
          name="moduleId"
          responseMeta={response => ({
            shown: response.dati?.length ?? 0,
            total: response.meta?.totale ?? 0,
          })}
        />
      </FormItem>
      <FormItem name="expression" label="Espressione">
        <InputBorder>
          <ConditionGroupInput name={`expression`} references={references} />
        </InputBorder>
      </FormItem>

      <FormItem
        name="title"
        label="Titolo del modulo"
        help="Permette di fornire un titolo identificativo aggiuntivo al modulo per permettere all'utente di distinguere moduli duplicati"
      >
        <AssignableExpressionInput
          field={{ type: Datatype.String }}
          references={references}
          name={'title'}
          block
          size="large"
        />
      </FormItem>

      <FormItem
        name="needUniqueModuleCode"
        label={null}
        layout={{ removeLabelSpace: true }}
        help="Impostando un codice, tutti i widget con lo stesso codice attiveranno il modulo un'unica volta."
      >
        <CheckboxInput name="needUniqueModuleCode" defaultChecked={false}>
          Imposta codice univoco di attivazione
        </CheckboxInput>
      </FormItem>
      {needUniqueModuleCode && (
        <FormItem name="uniqueModuleCode" label="Codice univoco di attivazione">
          <AssignableExpressionInput
            field={{ type: Datatype.String }}
            references={expressionReferences}
            name={'uniqueModuleCode'}
            block
            size="large"
          />
        </FormItem>
      )}
    </div>
  );
}
